/**
 * Telegram manager-side SenderGate for the opencode runtime (ADR-0047).
 *
 * On Claude Code the telegram-channel MCP composes the inbound-access leaves
 * (content, org boundary via TELEGRAM_ALLOWED_CHATS, peer classification) inline
 * in its poll loop and hands them to the ONE shared composer `decideInboundAccess`
 * (ENG-6236, `telegram-channel.ts` ~L4973). opencode has no in-session channel
 * MCP, so the manager must make the same decision before injecting a turn over
 * HTTP. This module is that composition, reusing the identical core primitives and
 * composer - no replicated policy, so no drift from the live Telegram handler.
 *
 * It is the Telegram analogue of `slack-sender-gate.ts`: an admit/deny wrapper for
 * the injection gate only. Command detection and the async peer rate-limiter are
 * intentionally out of scope (opencode does not dispatch Telegram slash commands
 * via this path, and the injection gate needs only admit/deny), so `command` is
 * never supplied. Telegram has no sender-policy mode system, so `senderPolicy` is
 * omitted and `sameOrg` is false: the composer yields only admit or drop.
 *
 * It runs on the RAW Telegram message (from.is_bot, entities, reply_to_message,
 * chat.id, ...) because the peer classifier needs those fields, which the
 * normalized InboundMessage discards - so the ingest consumer calls this on the
 * raw update and only normalizes an admitted message.
 */

import {
  classifyPeerMessage,
  type PeerClassifierConfig,
  type PeerClassifierMessage,
  type PeerClassifierSelf,
} from './telegram-peer-classifier.js';
import {
  decideInboundAccess,
  type InboundAccessInput,
} from './inbound-access.js';
import { isTelegramPrivateChat } from './telegram-chat-type.js';

/**
 * The raw Telegram `Message` fields the gate reads. A structural subset of the
 * Bot API object - the peer classifier consumes the same shape via
 * `PeerClassifierMessage`, and the org-boundary / content leaves need only
 * `chat.id` and the message text.
 */
export interface TelegramGateEvent {
  message_id: number;
  text?: string;
  caption?: string;
  from?: { id: number; username?: string; is_bot?: boolean };
  chat: { id: number; type: string };
  reply_to_message?: { message_id: number; from?: { id: number; is_bot?: boolean } };
  entities?: ReadonlyArray<{ type: string; offset: number; length: number }>;
  caption_entities?: ReadonlyArray<{ type: string; offset: number; length: number }>;
}

export interface TelegramGateDeps {
  /**
   * TELEGRAM_ALLOWED_CHATS as a set of chat-id strings. Empty ⇒ no chat gating
   * (every chat is admitted at the org-boundary axis), exactly as the MCP treats
   * an empty `ALLOWED_CHATS`.
   */
  allowedChats: ReadonlySet<string>;
  /**
   * CS-1628 — when true, only 1:1 (`private`) chats are admitted; group,
   * supergroup and channel traffic is dropped at ingestion, before the message
   * reaches the model.
   *
   * OPT-IN. Absent or false is exactly today's behaviour. This is the control a
   * customer asked for after an unknown user added their agent to a supergroup:
   * the allowlist answers "which chats", which requires knowing ids for groups
   * you have never seen and cannot enumerate, and this answers "no groups at
   * all", which does not.
   *
   * Stronger than instructing the model not to engage, in a way that matters: a
   * dropped message never reaches the model, so there is no text for an attacker
   * to write into. And because the drop happens before a pending marker exists,
   * it cannot strand one the way suppressing a REPLY does (CS-1629).
   */
  privateChatsOnly?: boolean;
  /** Peer-agent classifier config (mode, group ids, peer roster, kill switch). */
  peerConfig: PeerClassifierConfig;
  /**
   * Our own bot identity (getMe). `bot_id`/`bot_username` may be null when getMe
   * has not resolved yet; the classifier treats that as `self_resolution_pending`
   * (a drop) rather than guessing, matching the MCP.
   */
  self: PeerClassifierSelf;
}

/**
 * Structurally the opencode bridge's `GateDecision` (kept local to avoid a
 * governance -> framework import). `admit:false` carries the composer's drop
 * reason for telemetry.
 */
export type TelegramGateDecision = { admit: true } | { admit: false; reason: string };

/**
 * Decide whether a raw Telegram inbound message should be injected into the
 * agent. Faithful to `decideInboundAccess`'s precedence: content filter -> self
 * -> org boundary -> peer governance -> admit.
 */
export function evaluateTelegramInboundGate(
  evt: TelegramGateEvent,
  deps: TelegramGateDeps,
): TelegramGateDecision {
  const isBot = Boolean(evt.from?.is_bot);
  const text = (evt.text ?? evt.caption ?? '').trim();
  const chatId = String(evt.chat.id);

  // Leaf 1: content filter. An empty message (no text, no caption) is channel
  // noise. The MCP filters empties upstream and passes `content:{forward:true}`;
  // the manager has no such prefilter, so it is computed here.
  const content: InboundAccessInput['content'] =
    text.length > 0 ? { forward: true } : { forward: false, reason: 'empty' };

  // Leaf 2: peer-agent governance (bot-authored messages only). The classifier
  // (same kind-union as Slack) also supplies the sole self-echo signal Telegram
  // has - getUpdates should never echo our own outbound, but if it did the
  // classifier reports `self` and the composer drops it before anything else.
  let peerLeaf: InboundAccessInput['peer'];
  let isSelf = false;
  if (isBot) {
    const classifierMsg: PeerClassifierMessage = {
      message_id: evt.message_id,
      text: evt.text,
      caption: evt.caption,
      from: evt.from
        ? { id: evt.from.id, username: evt.from.username, is_bot: evt.from.is_bot }
        : undefined,
      chat: { id: evt.chat.id, type: evt.chat.type },
      reply_to_message: evt.reply_to_message
        ? {
            message_id: evt.reply_to_message.message_id,
            from: evt.reply_to_message.from
              ? { id: evt.reply_to_message.from.id, is_bot: evt.reply_to_message.from.is_bot }
              : undefined,
          }
        : undefined,
      entities: evt.entities,
      caption_entities: evt.caption_entities,
    };
    const c = classifyPeerMessage(classifierMsg, deps.peerConfig, deps.self);
    isSelf = c.kind === 'self';
    peerLeaf = { kind: c.kind, reason: c.kind === 'drop' ? c.reason : undefined };
  }

  const decision = decideInboundAccess({
    content,
    // Telegram's only self signal is the peer classifier (Slack catches self at
    // evt.user===botUserId; Teams inside its activity filter).
    isSelf,
    isBot,
    peer: peerLeaf,
    // TELEGRAM_ALLOWED_CHATS is Telegram's org-boundary axis. An unlisted chat is
    // external ⇒ silent drop.
    orgBoundary:
      deps.allowedChats.size > 0 && !deps.allowedChats.has(chatId)
        ? { forward: false }
        : { forward: true },
    // CS-1628: the chat-kind axis. Only ever restricts - when the policy is off
    // this is `{ forward: true }`, which the composer treats identically to the
    // field being absent.
    chatType:
      deps.privateChatsOnly && !isTelegramPrivateChat(evt.chat.type)
        ? { forward: false }
        : { forward: true },
    // No sender-policy mode system and no command dispatch on the injection gate.
    sameOrg: false,
  });

  if (decision.kind === 'drop') return { admit: false, reason: decision.reason };
  return { admit: true };
}
